import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { api } from '../api';
import { ErrorBox, QueryView } from '../components/ui';
import { useLocale } from '../i18n';
import { qk } from '../queryClient';

/**
 * Administration → Email (owner, decision 93): whether the server can send (the provider's keys), the switch that lets
 * invoices and statements go to customers, the sender's name and the reply-to address.
 */
export function EmailSettingsPage() {
  const { t } = useLocale();
  const qc = useQueryClient();
  const status = useQuery({ queryKey: qk.emailStatus, queryFn: ({ signal }) => api.emailStatus(signal) });
  const [form, setForm] = useState({ live: false, fromName: '', replyTo: '' });
  useEffect(() => {
    if (status.data) setForm({ live: status.data.live, fromName: status.data.fromName, replyTo: status.data.replyTo ?? '' });
  }, [status.data]);
  const save = useMutation({
    mutationFn: () => api.saveEmailSettings({ live: form.live, fromName: form.fromName.trim(), replyTo: form.replyTo.trim() || null }),
    onSuccess: (s) => qc.setQueryData(qk.emailStatus, s),
  });
  const replyOk = !form.replyTo.trim() || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.replyTo.trim());

  return (
    <div className="desk-page" data-testid="email-settings">
      <header className="desk-head">
        <div>
          <h1>{t('emailSettingsTitle')}</h1>
          <p className="muted">{t('emailSettingsHint')}</p>
        </div>
      </header>
      <QueryView query={status}>
        {(s) => (
          <div className="stack">
            <section className="panel stack-sm">
              <h2>{t('emailServer')}</h2>
              {s.configured ? (
                <p className="ok-band" role="status">
                  {t('emailConfigured').replace('{from}', s.from ?? '')}
                </p>
              ) : (
                <>
                  <p className="warn-band">{t('emailNotSetUp')}</p>
                  <p className="muted">{t('emailSetupHint')}</p>
                </>
              )}
            </section>
            <form
              className="panel stack-sm"
              onSubmit={(e) => {
                e.preventDefault();
                if (form.fromName.trim() && replyOk) save.mutate();
              }}
            >
              <h2>{t('emailToCustomers')}</h2>
              <label className="check">
                <input type="checkbox" checked={form.live} onChange={(e) => setForm({ ...form, live: e.target.checked })} /> {t('emailLiveLabel')}
              </label>
              <p className="muted">{t('emailLiveHint')}</p>
              <label className="field">
                <span>{t('emailFromName')}</span>
                <input type="text" maxLength={80} value={form.fromName} onChange={(e) => setForm({ ...form, fromName: e.target.value })} />
              </label>
              <label className="field">
                <span>{t('emailReplyTo')}</span>
                <input type="email" maxLength={200} value={form.replyTo} onChange={(e) => setForm({ ...form, replyTo: e.target.value })} aria-invalid={!replyOk || undefined} />
              </label>
              {save.error ? <ErrorBox error={save.error} /> : null}
              {save.isSuccess ? (
                <p className="note" role="status">
                  {t('allSaved')}
                </p>
              ) : null}
              <div className="row-actions">
                <button type="submit" className="btn btn-primary" disabled={save.isPending || !form.fromName.trim() || !replyOk}>
                  {t('save')}
                </button>
              </div>
            </form>
          </div>
        )}
      </QueryView>
    </div>
  );
}
